import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { buildAppMeta, SITE_URL } from "@/lib/seo";
import { supabase } from "@/integrations/supabase/client";
import { RequireAuth } from "@/components/RequireAuth";
import { useAuth } from "@/hooks/useAuth";
import { useOnboarding } from "@/hooks/useOnboarding";
import { OnboardingHeader } from "@/components/onboarding/OnboardingHeader";
import { ScreenSport } from "@/components/onboarding/ScreenSport";
import { ScreenPayoff } from "@/components/onboarding/ScreenPayoff";
import { ScreenProfile, type ProfileInitialValues } from "@/components/onboarding/ScreenProfile";
import { ScreenClients, type FirstClient } from "@/components/onboarding/ScreenClients";
import { ScreenCalendar } from "@/components/onboarding/ScreenCalendar";
import { ScreenFinale } from "@/components/onboarding/ScreenFinale";
import { SPORT_CONTENT, type OnboardingSport } from "@/components/onboarding/sport-content";

type Search = { step?: string; google?: string; reason?: string };

export const Route = createFileRoute("/app/onboarding")({
  head: () => ({
    meta: buildAppMeta({
      title: "Welcome — CoachQuick",
      description:
        "Get started with CoachQuick: pick your sport, set up your profile, add clients, and log your first session.",
      path: "/app/onboarding",
    }),
    links: [{ rel: "canonical", href: `${SITE_URL}/app/onboarding` }],
  }),
  validateSearch: (s: Record<string, unknown>): Search => ({
    step: typeof s.step === "string" ? s.step : undefined,
    google: typeof s.google === "string" ? s.google : undefined,
    reason: typeof s.reason === "string" ? s.reason : undefined,
  }),
  component: () => (
    <RequireAuth role="coach">
      <OnboardingPage />
    </RequireAuth>
  ),
});

// 0 sport picker · 1 payoff story · 2 profile · 3 clients · 4 calendar · 5 finale
type Screen = 0 | 1 | 2 | 3 | 4 | 5;

type ProfileRow = ProfileInitialValues & { sport: string | null };

function isOnboardingSport(v: unknown): v is OnboardingSport {
  return typeof v === "string" && v in SPORT_CONTENT;
}

function OnboardingPage() {
  const nav = useNavigate();
  const search = Route.useSearch();
  const { user } = useAuth();
  const onboarding = useOnboarding();
  const { state, isLoading, ensureRow, markStep, skip } = onboarding;

  const [screen, setScreen] = useState<Screen | null>(null);
  const [sport, setSport] = useState<OnboardingSport | null>(null);
  const [autofill, setAutofill] = useState(true);
  const [firstClient, setFirstClient] = useState<FirstClient | null>(null);
  const initRef = useRef(false);

  const profileQuery = useQuery<ProfileRow | null>({
    queryKey: ["onboarding-profile", user?.id],
    enabled: !!user,
    queryFn: async () => {
      if (!user) return null;
      const { data } = await supabase
        .from("coach_profile" as never)
        .select("sport, full_name, title, phone, website, sign_off, avatar_url")
        .eq("coach_id", user.id)
        .maybeSingle();
      const row = data as Record<string, string | null> | null;
      if (!row) return null;
      return {
        sport: row.sport ?? null,
        full_name: row.full_name ?? "",
        title: row.title ?? "",
        phone: row.phone ?? "",
        website: row.website ?? "",
        sign_off: row.sign_off ?? "",
        avatar_url: row.avatar_url ?? "",
      };
    },
  });

  // Decide the starting screen once both queries settle: an OAuth return jumps
  // to the calendar step; otherwise resume at the first incomplete step.
  useEffect(() => {
    if (initRef.current) return;
    if (isLoading || profileQuery.isLoading) return;
    initRef.current = true;

    ensureRow().catch(() => {});

    const persistedSport = profileQuery.data?.sport;
    if (isOnboardingSport(persistedSport)) setSport(persistedSport);

    if (search.step === "calendar") {
      if (search.google === "connected") {
        toast.success("Google Calendar connected");
        markStep(4).catch(() => {});
      } else if (search.google === "error") {
        toast.error(`Couldn't connect Google Calendar${search.reason ? `: ${search.reason}` : ""}`);
      }
      setScreen(4);
      nav({ to: "/app/onboarding", search: {}, replace: true });
      return;
    }

    const flags = state;
    const anyProgress =
      !!flags &&
      (flags.step1_clients_done ||
        flags.step2_library_done ||
        flags.step3_log_done ||
        flags.step4_calendar_done ||
        flags.step_profile_done);

    // No step completed yet → start at the sport picker (fresh signup, or a
    // restart, which clears the flags but keeps the persisted sport so the
    // picker pre-selects it). Once any step is done, resume at the first
    // incomplete step and skip the picker/payoff intro.
    if (!anyProgress) setScreen(0);
    else if (!flags?.step_profile_done) setScreen(2);
    else if (!flags?.step1_clients_done) setScreen(3);
    else if (!flags?.step4_calendar_done) setScreen(4);
    else setScreen(5);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, profileQuery.isLoading]);

  async function skipToApp() {
    try {
      await skip();
    } catch {
      // Best-effort: still let them into the app.
    }
    nav({ to: "/app" });
  }

  const activeSport: OnboardingSport = sport ?? "pickleball";

  const header =
    screen === 0 || screen === 1
      ? { right: { label: "Skip to the app", onClick: skipToApp } }
      : screen === 2
        ? {
            progress: { filled: 1, label: "Step 1 of 4" },
            right: { label: "Skip this step", onClick: () => setScreen(3) },
          }
        : screen === 3
          ? {
              progress: { filled: 2, label: "Step 2 of 4" },
              right: { label: "Skip this step", onClick: () => setScreen(4) },
            }
          : screen === 4
            ? {
                progress: { filled: 3, label: "Step 3 of 4" },
                right: { label: "Skip this step", onClick: () => setScreen(5) },
              }
            : { progress: { filled: 4, label: "Last step" } };

  const profileInitial: ProfileInitialValues = profileQuery.data ?? {
    full_name: (user?.user_metadata?.full_name as string | undefined) ?? "",
    title: "",
    phone: "",
    website: "",
    sign_off: "",
    avatar_url: "",
  };

  return (
    <div className="min-h-screen bg-background px-3 py-4 md:px-6 md:py-8">
      <div className="mx-auto max-w-5xl overflow-hidden rounded-2xl border border-border bg-card shadow-soft">
        {screen === null ? (
          <div className="flex h-[60vh] items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <OnboardingHeader {...header} />
            {screen === 0 && (
              <ScreenSport
                sport={sport}
                onPickSport={setSport}
                autofill={autofill}
                onToggleAutofill={setAutofill}
                onDone={() => setScreen(1)}
              />
            )}
            {screen === 1 && (
              <ScreenPayoff
                sport={activeSport}
                onContinue={() => setScreen(2)}
                onSkip={skipToApp}
              />
            )}
            {screen === 2 && (
              <ScreenProfile
                sport={activeSport}
                initial={{
                  ...profileInitial,
                  full_name:
                    profileInitial.full_name ||
                    ((user?.user_metadata?.full_name as string | undefined) ?? ""),
                }}
                onDone={() => setScreen(3)}
                onLater={() => setScreen(3)}
              />
            )}
            {screen === 3 && (
              <ScreenClients
                sport={activeSport}
                onDone={(client) => {
                  setFirstClient(client);
                  setScreen(4);
                }}
                onLater={() => setScreen(4)}
              />
            )}
            {screen === 4 && <ScreenCalendar sport={activeSport} onDone={() => setScreen(5)} />}
            {screen === 5 && <ScreenFinale sport={activeSport} firstClient={firstClient} />}
          </>
        )}
      </div>
    </div>
  );
}
